import { For, JSX } from 'solid-js';
import { Dynamic } from 'solid-js/web';
import { SetStoreFunction } from 'solid-js/store';
import { PaneLayouts, PaneManagerConfig } from '../models/pane-manager-models';

export interface LayoutSelectorProps<T> {
  layouts: PaneLayouts<T>;
  setLayouts: SetStoreFunction<PaneLayouts<T>>;
  /** Maps a content config to an icon name string for rendering in the layout preview. */
  iconResolver(config: T): string;
  /** Optional title text displayed above the layout options. */
  title?: string;
  /** Optional custom icon renderer. If not provided, renders a basic icon element. */
  renderIcon?(iconName: string): JSX.Element;
  /** Optional resolver that maps a content config to a `data-testid` string for each cell. */
  testIdResolver?(config: T): string;
}

/**
 * Generic layout selector component that renders a grid of selectable layout presets.
 * Each preset shows a visual preview of its pane arrangement with icons.
 */
export const LayoutSelector = <T,>(
  props: LayoutSelectorProps<T>
): JSX.Element => {
  const LayoutView = (viewProps: {
    layout: PaneManagerConfig<T>;
  }): JSX.Element => {
    return (
      <div class="flex flex-col w-full h-full overflow-hidden rounded-md border border-gray-0">
        <For each={viewProps.layout.paneRows}>
          {(row) => (
            <div class="flex flex-1 border-b last:border-b-0 border-gray-0">
              <For each={row.cells}>
                {(cell) => (
                  <div
                    class="flex-1 flex items-center justify-center border-r last:border-r-0 border-gray-0"
                    data-testid={props.testIdResolver?.(cell.contentConfig)}
                  >
                    {props.renderIcon ? (
                      props.renderIcon(props.iconResolver(cell.contentConfig))
                    ) : (
                      <span class={props.iconResolver(cell.contentConfig)} />
                    )}
                  </div>
                )}
              </For>
            </div>
          )}
        </For>
      </div>
    );
  };

  return (
    <div>
      {props.title && <div class="px-2 py-1 font-semibold">{props.title}</div>}
      <div class="mx-2 mt-2 grid grid-cols-[repeat(2,minmax(120px,1fr))] gap-4">
        <For each={props.layouts.layouts}>
          {(layout) => (
            <div
              data-testid={`layout-option`}
              aria-selected={props.layouts.activeLayoutLabel === layout.label}
              class={`border rounded overflow-hidden
                shadow-[0_0_0.2rem_rgba(0,0,0,0.2)]
                hover:shadow-[0_0_0.375rem_rgba(0,0,0,0.2)]
                ${
                  props.layouts.activeLayoutLabel === layout.label
                    ? 'border-blue'
                    : 'border-gray-0 cursor-pointer'
                }
                `}
              onClick={() => {
                props.setLayouts('activeLayoutLabel', layout.label);
              }}
            >
              <div class="aspect-[16/9] opacity-60">
                <Dynamic component={LayoutView} layout={layout} />
              </div>
              <div
                class={`p-2
                  ${
                    props.layouts.activeLayoutLabel === layout.label
                      ? 'bg-blue text-white'
                      : 'bg-gray-light'
                  }`}
              >
                {layout.label}
              </div>
            </div>
          )}
        </For>
      </div>
    </div>
  );
};
